import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  ArrowLeft, MapPin, Calendar, TrendingUp, Award, 
  BadgeCheck, MessageCircle, Share2, ThumbsUp, MoreVertical, UserPlus, UserCheck
} from 'lucide-react';
import { DUMMY_REPORTS, GlobalReport } from '../data/mockData';
import { ReportCard } from './ReportCard';
import { toast } from 'sonner';
import { reportsService } from '../services/api';
import { copyToClipboard } from '../utils/clipboard';

interface UserProfileViewProps {
  userName: string;
  onBack: () => void;
}

export function UserProfileView({ userName, onBack }: UserProfileViewProps) {
  const [userReports, setUserReports] = React.useState<GlobalReport[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [isFollowing, setIsFollowing] = React.useState(false);
  
  // Fetch user reports from backend
  React.useEffect(() => {
    const fetchUserReports = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log(`📡 Fetching reports for user: ${userName}`);
        const { reports } = await reportsService.getUserReports(userName);
        
        console.log(`✅ Loaded ${reports.length} reports for ${userName}`);
        setUserReports(reports || []);
      } catch (err: any) {
        console.error('❌ Error loading user reports:', err);
        setError(err.message || 'Failed to load user reports');
        
        // Fallback to mock data if backend fails
        const fallbackReports = DUMMY_REPORTS.filter(report => report.userName === userName);
        setUserReports(fallbackReports);
        
        if (fallbackReports.length === 0) {
          toast.error('Unable to load user reports', {
            description: 'Please check your connection and try again.'
          });
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserReports();
  }, [userName]);
  
  // Calculate user stats
  const totalReports = userReports.length;
  const totalLikes = userReports.reduce((sum, report) => sum + (report.likes || 0), 0);
  const totalComments = userReports.reduce((sum, report) => sum + (report.comments || 0), 0);
  const trustScore = userReports[0]?.userTrustScore || 0;
  const isVerified = trustScore > 1000;
  
  // Get user info from first report
  const userInfo = userReports[0] || {
    userName: userName,
    userAvatar: '',
    country: 'Nigeria',
    userTrustScore: 0,
    timestamp: 'New member'
  };

  // Estimate join date (mock - in real app would come from backend)
  const joinDate = 'January 2026';

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="min-h-screen bg-gray-50 pb-20"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white sticky top-0 z-30 shadow-lg">
        <div className="px-4 pt-12 pb-6">
          <button
            onClick={onBack}
            className="mb-4 p-2 rounded-xl bg-white/20 hover:bg-white/30 active:scale-95 transition-all backdrop-blur-sm"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden border-4 border-white/30 shadow-xl flex-shrink-0">
              {userInfo.userAvatar ? (
                <img src={userInfo.userAvatar} alt={userName} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-black text-3xl">{userName.charAt(0).toUpperCase()}</span>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl font-black">{userName}</h1>
                {isVerified && (
                  <BadgeCheck className="w-6 h-6 text-blue-400 fill-blue-400/20" />
                )}
              </div>
              
              <div className="flex items-center gap-4 text-sm text-white/90">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>{userInfo.country}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Joined {joinDate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 px-4 pb-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
            <div className="text-2xl font-black mb-1">{totalReports}</div>
            <div className="text-xs text-white/80 font-bold">Reports</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
            <div className="text-2xl font-black mb-1">{totalLikes}</div>
            <div className="text-xs text-white/80 font-bold">Verifications</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 text-center">
            <div className="text-2xl font-black mb-1">{trustScore}</div>
            <div className="text-xs text-white/80 font-bold">Trust Score</div>
          </div>
        </div>
      </div>

      {/* Profile Actions */}
      <div className="px-4 py-4 bg-white border-b border-gray-100">
        <div className="flex gap-3">
          <button
            onClick={() => {
              setIsFollowing(!isFollowing);
              toast.success(
                isFollowing ? 'Unfollowed' : 'Now Following!', 
                { 
                  description: isFollowing 
                    ? `You unfollowed ${userName}` 
                    : `You'll get updates when ${userName} posts`,
                  icon: isFollowing ? '👋' : '🔔'
                }
              );
            }}
            className={`flex-1 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2 ${
              isFollowing
                ? 'bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-green-500/20'
            }`}
          >
            {isFollowing ? (
              <>
                <UserCheck className="w-5 h-5" />
                Following
              </>
            ) : (
              <>
                <UserPlus className="w-5 h-5" />
                Follow
              </>
            )}
          </button>
          <button
            onClick={() => toast.info('Message feature coming soon!')}
            className="flex-1 bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-50 active:scale-95 transition-all"
          >
            Message
          </button>
          <button
            onClick={async () => {
              const shareText = `Check out ${userName}'s profile on Sentlo! Trust Score: ${trustScore}, ${totalReports} reports`;
              const shareUrl = `${window.location.origin}`;
              
              if (navigator.share) {
                try {
                  await navigator.share({
                    title: `${userName} on Sentlo`,
                    text: shareText,
                    url: shareUrl
                  });
                  toast.success('Shared successfully!');
                } catch (err: any) {
                  // User cancelled or error occurred
                  if (err.name !== 'AbortError') {
                    // Fallback to clipboard
                    const success = await copyToClipboard(`${shareText}\n\n${shareUrl}`);
                    if (success) {
                      toast.success('Profile link copied to clipboard!');
                    }
                  }
                }
              } else {
                // Browser doesn't support Web Share API
                const success = await copyToClipboard(`${shareText}\n\n${shareUrl}`);
                if (success) {
                  toast.success('Profile link copied to clipboard!');
                } else {
                  toast.error('Failed to copy link');
                }
              }
            }}
            className="px-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 active:scale-95 transition-all flex items-center justify-center"
            title="Share profile"
          >
            <Share2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Achievements Section */}
      {isVerified && (
        <div className="px-4 py-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                Verified Reporter
                <BadgeCheck className="w-5 h-5 text-blue-500" />
              </h3>
              <p className="text-sm text-gray-600">Trusted community member with {trustScore}+ trust score</p>
            </div>
          </div>
        </div>
      )}

      {/* Reports Section */}
      <div className="px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black text-gray-900">Reports by {userName}</h2>
          <span className="text-sm text-gray-500">{totalReports} total</span>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-full border-4 border-green-500 border-t-transparent animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading posts...</p>
          </div>
        ) : userReports.length > 0 ? (
          <div className="space-y-4">
            {userReports.map((report) => (
              <ReportCard
                key={report.id}
                report={report}
                onLike={(id) => toast.success('Verified!')}
                onComment={(id) => toast.info('Comment feature coming soon!')}
                onGift={(id) => toast.info('Gift feature coming soon!')}
                isGiftingEnabled={false}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No Reports Yet</h3>
            <p className="text-sm text-gray-600">
              {userName} hasn't posted any reports yet.
            </p>
          </div>
        )}
      </div>

      {/* Bottom Spacer for Navigation */}
      <div className="h-20" />
    </motion.div>
  );
}